(() => {
  /*
    Theme-side free shipping bridge for PDP bundle tiers.

    Shopify's native free-shipping discounts cannot be constrained by a line
    item property, and we are intentionally avoiding a Shopify Function/app for
    this small rule. The PDP bundle widget writes hidden bundle markers onto
    each bundle line. This script reads the cart immediately before checkout,
    cleans invalid partial bundles, and routes the shopper through Shopify's
    checkout discount URL when a complete qualifying bundle is present.

    The checkout URL is the only moment where the native discount code needs to
    be applied. Cart rendering, prices, and bundle creation stay unchanged.
  */
  const FREE_SHIPPING_CODE = "FREE-SHIPPING-LIA";
  const CLEAR_DISCOUNT_CODE = "REMOVE_SHOPIFY_CODE";
  const DISCOUNT_COOKIE_NAME = "discount_code";
  const AUTO_APPLIED_KEY = "maison_free_shipping_lia_auto_applied";
  const BUNDLE_DISCOUNT_PROPERTY = "_pdp_bundle_widget_discount";
  const BUNDLE_NONCE_PROPERTY = "_pdp_bundle_widget_nonce";
  const BUNDLE_GIFT_PROPERTY = "_pdp_bundle_widget_gift";
  const BUNDLE_GIFT_QUANTITY_PROPERTY = "_pdp_bundle_widget_gift_quantity";

  // Prevent double-clicks or simultaneous form submits from racing to checkout.
  let isNavigatingToCheckout = false;

  function getCountFromDiscount(discount) {
    const match = String(discount || "").trim().match(/^tier-(\d+)$/);
    if (!match) return 0;

    const count = parseInt(match[1], 10);
    return Number.isNaN(count) || count < 1 ? 0 : count;
  }

  function getRoutesRoot() {
    const root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : "/";

    return root.endsWith("/") ? root : `${root}/`;
  }

  function getCheckoutUrl(discountCodes = []) {
    const baseUrl = `${getRoutesRoot()}checkout`;
    const cleanCodes = uniqueCodes(discountCodes);

    if (!cleanCodes.length) return baseUrl;

    /*
      Shopify accepts a comma-separated `discount` query value at checkout. We
      preserve visible/applied customer codes and add FREE-SHIPPING-LIA only
      when the current cart qualifies.
    */
    return `${baseUrl}?discount=${encodeURIComponent(cleanCodes.join(","))}`;
  }

  function normalizeCode(code) {
    return String(code || "").trim().toUpperCase();
  }

  function uniqueCodes(codes) {
    const seen = new Set();
    const unique = [];

    codes.forEach((code) => {
      const cleanCode = String(code || "").trim();
      const normalizedCode = normalizeCode(cleanCode);

      if (!cleanCode || seen.has(normalizedCode)) return;

      seen.add(normalizedCode);
      unique.push(cleanCode);
    });

    return unique;
  }

  function addCode(codes, code) {
    const normalizedCode = normalizeCode(code);

    if (!normalizedCode) return;
    if (normalizedCode === normalizeCode(CLEAR_DISCOUNT_CODE)) return;
    if (codes.some((existingCode) => normalizeCode(existingCode) === normalizedCode)) return;

    codes.push(String(code).trim());
  }

  function addCodesFromString(codes, value) {
    String(value || "")
      .split(",")
      .forEach((code) => addCode(codes, code));
  }

  function toArray(value) {
    return Array.isArray(value) ? value : [];
  }

  function getPropertyValue(properties, propertyName) {
    if (!properties) return "";

    /*
      `/cart.js` usually exposes line item properties as an object, but some
      Shopify surfaces/apps serialize properties as arrays. Supporting both
      shapes keeps the eligibility check tolerant of future cart integrations.
    */
    if (Array.isArray(properties)) {
      const matchingProperty = properties.find((property) => {
        if (Array.isArray(property)) return property[0] === propertyName;
        return property && (property.name === propertyName || property.key === propertyName);
      });

      if (!matchingProperty) return "";
      if (Array.isArray(matchingProperty)) return matchingProperty[1];

      return matchingProperty.value;
    }

    return properties[propertyName];
  }

  function getCleanProperty(item, propertyName) {
    return String(getPropertyValue(item.properties, propertyName) || "").trim();
  }

  function isGiftLine(item) {
    return getCleanProperty(item, BUNDLE_GIFT_PROPERTY) === "true";
  }

  function getExpectedGiftQuantity(item) {
    const quantity = parseInt(getCleanProperty(item, BUNDLE_GIFT_QUANTITY_PROPERTY), 10);
    return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
  }

  function hasExpectedGiftQuantity(line) {
    const quantity = parseInt(line.item.quantity, 10);
    if (Number.isNaN(quantity) || quantity < 1) return false;

    return quantity === getExpectedGiftQuantity(line.item);
  }

  function getBundleGroups(cart) {
    const groups = new Map();

    (cart.items || []).forEach((item) => {
      const nonce = getCleanProperty(item, BUNDLE_NONCE_PROPERTY);
      if (!nonce) return;

      if (!groups.has(nonce)) {
        groups.set(nonce, {
          nonce,
          lines: [],
          paidLines: [],
          giftLines: [],
        });
      }

      const line = {
        item,
        key: item.key,
        isGift: isGiftLine(item),
      };
      const group = groups.get(nonce);

      group.lines.push(line);
      if (line.isGift) group.giftLines.push(line);
      else group.paidLines.push(line);
    });

    return Array.from(groups.values());
  }

  function getExpectedDiscount(group) {
    const discounts = group.paidLines
      .map((line) => getCleanProperty(line.item, BUNDLE_DISCOUNT_PROPERTY))
      .filter(Boolean);

    if (!discounts.length) return "";

    const expectedDiscount = discounts[0];
    return discounts.every((discount) => discount === expectedDiscount) ? expectedDiscount : "";
  }

  function getExpectedCount(group) {
    return getCountFromDiscount(getExpectedDiscount(group));
  }

  function isCompleteBundleGroup(group) {
    const expectedDiscount = getExpectedDiscount(group);
    const expectedCount = getExpectedCount(group);
    const paidQuantity = group.paidLines.reduce((sum, line) => {
      const quantity = parseInt(line.item.quantity, 10);
      return sum + (Number.isNaN(quantity) ? 0 : quantity);
    }, 0);

    if (!expectedDiscount) return false;
    if (!expectedCount || paidQuantity === 0 || paidQuantity % expectedCount !== 0) return false;

    return (
      group.paidLines.every((line) => {
        return getCleanProperty(line.item, BUNDLE_DISCOUNT_PROPERTY) === expectedDiscount;
      }) && group.giftLines.every(hasExpectedGiftQuantity)
    );
  }

  function hasEligibleCompleteBundle(cart) {
    if (
      window.MaisonBundleCart &&
      typeof window.MaisonBundleCart.hasEligibleCompleteBundle === "function"
    ) {
      return window.MaisonBundleCart.hasEligibleCompleteBundle(cart);
    }

    /*
      The business rule is intentionally narrow: complete PDP bundle groups
      with at least 3 paid items unlock this free shipping code.
    */
    return getBundleGroups(cart).some((group) => {
      if (!isCompleteBundleGroup(group)) return false;

      const discount = getCleanProperty(group.paidLines[0].item, BUNDLE_DISCOUNT_PROPERTY);
      return getCountFromDiscount(discount) >= 3;
    });
  }

  function getInvalidBundleKeys(cart) {
    return getBundleGroups(cart)
      .filter((group) => !isCompleteBundleGroup(group))
      .flatMap((group) => group.lines.map((line) => line.key).filter(Boolean));
  }

  async function cleanupInvalidBundles(cart) {
    if (
      window.MaisonBundleCart &&
      typeof window.MaisonBundleCart.cleanupInvalidBundles === "function"
    ) {
      const result = await window.MaisonBundleCart.cleanupInvalidBundles();
      return result && result.cart ? result.cart : fetchCart();
    }

    const invalidKeys = getInvalidBundleKeys(cart);
    if (!invalidKeys.length) return cart;

    const body = {
      updates: [...new Set(invalidKeys)].reduce((updates, key) => {
        updates[key] = 0;
        return updates;
      }, {}),
    };

    const response = await fetch(`${getRoutesRoot()}cart/update.js`, {
      credentials: "same-origin",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error("Unable to clean bundle cart lines");

    return fetchCart();
  }

  function collectAppliedDiscountCodes(cart) {
    const codes = [];

    function addDiscountApplication(discountApplication) {
      if (!discountApplication || !discountApplication.title) return;
      if (discountApplication.type && discountApplication.type !== "discount_code") return;

      addCode(codes, discountApplication.title);
    }

    function addCartDiscountCode(discountCode) {
      if (!discountCode) return;

      if (typeof discountCode === "string") {
        addCodesFromString(codes, discountCode);
        return;
      }

      addCodesFromString(codes, discountCode.code || discountCode.title);
    }

    toArray(cart.cart_level_discount_applications).forEach(addDiscountApplication);
    toArray(cart.discount_codes).forEach(addCartDiscountCode);
    toArray(cart.items).forEach((item) => {
      toArray(item.line_level_discount_allocations).forEach((allocation) => {
        addDiscountApplication(allocation.discount_application);
      });
    });

    /*
      `/cart.js` is the source of truth after Shopify accepts a discount, but a
      freshly entered code can also be visible in the custom discount UI before
      every cart section has re-rendered. Reading the pills lets us preserve
      those shopper-entered codes when we add or remove the free-shipping code.
    */
    document.querySelectorAll("#applied-discounts .discount-pill[data-code]").forEach((pill) => {
      addCode(codes, pill.getAttribute("data-code"));
    });

    addCodesFromString(codes, getCookieValue(DISCOUNT_COOKIE_NAME));

    return codes;
  }

  function getCookieValue(name) {
    try {
      const cookie = document.cookie
        .split(";")
        .map((item) => item.trim())
        .find((item) => item.startsWith(`${name}=`));

      if (!cookie) return "";

      return decodeURIComponent(cookie.slice(name.length + 1));
    } catch (error) {
      return "";
    }
  }

  function markAutoApplied() {
    try {
      window.localStorage.setItem(AUTO_APPLIED_KEY, "true");
    } catch (error) {}
  }

  function clearAutoApplied() {
    try {
      window.localStorage.removeItem(AUTO_APPLIED_KEY);
    } catch (error) {}
  }

  async function fetchCart() {
    const response = await fetch(`${getRoutesRoot()}cart.js`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) throw new Error("Unable to load cart");

    return response.json();
  }

  async function updateCartDiscounts(discountCodes = []) {
    const response = await fetch(`${getRoutesRoot()}cart/update.js`, {
      credentials: "same-origin",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({
        discount: uniqueCodes(discountCodes).join(","),
      }),
    });

    if (!response.ok) throw new Error("Unable to update cart discounts");

    return response.json();
  }

  function navigateTo(url) {
    window.location.href = url;
  }

  async function goToCheckout() {
    if (isNavigatingToCheckout) return;

    isNavigatingToCheckout = true;

    try {
      /*
        We fetch the cart at click/submit time instead of relying on Liquid
        flags. Cart drawer and cart notification markup can be re-rendered by
        AJAX, and the bundle item may be added moments before checkout.
      */
      let cart = await fetchCart();
      cart = await cleanupInvalidBundles(cart);
      const appliedCodes = collectAppliedDiscountCodes(cart);

      if (hasEligibleCompleteBundle(cart)) {
        addCode(appliedCodes, FREE_SHIPPING_CODE);
        markAutoApplied();
        navigateTo(getCheckoutUrl(appliedCodes));
        return;
      }

      const hasFreeShippingCode = appliedCodes.some(
        (code) => normalizeCode(code) === normalizeCode(FREE_SHIPPING_CODE)
      );

      if (hasFreeShippingCode) {
        /*
          If the qualifying bundle was removed after we auto-applied the code,
          remove only our free-shipping code and keep any other detected
          shopper-entered or shareable-link codes.
        */
        const remainingCodes = appliedCodes.filter(
          (code) => normalizeCode(code) !== normalizeCode(FREE_SHIPPING_CODE)
        );

        clearAutoApplied();
        try {
          await updateCartDiscounts(remainingCodes);
        } catch (error) {}
        navigateTo(getCheckoutUrl(remainingCodes));
        return;
      }

      clearAutoApplied();
      navigateTo(getCheckoutUrl(appliedCodes));
    } catch (error) {
      // Checkout should never be blocked by this helper. If cart detection fails, use normal checkout.
      navigateTo(getCheckoutUrl());
    }
  }

  function isCheckoutSubmit(form, submitter) {
    if (!form || !form.matches) return false;

    const formId = form.getAttribute("id");
    const isKnownCartForm = ["cart", "CartDrawer-Form", "cart-notification-form"].includes(formId);
    const isCheckoutSubmitter =
      submitter &&
      (submitter.getAttribute("name") === "checkout" ||
        submitter.getAttribute("id") === "checkout" ||
        submitter.getAttribute("id") === "CartDrawer-Checkout");

    return isKnownCartForm && (isCheckoutSubmitter || !submitter);
  }

  document.addEventListener(
    "submit",
    (event) => {
      const submitter = event.submitter || document.activeElement;

      if (!isCheckoutSubmit(event.target, submitter)) return;

      /*
        Use delegated capture-phase handling because the cart drawer and cart
        notification replace their inner HTML after AJAX cart updates. Direct
        listeners on the checkout buttons would be lost after those renders.
      */
      event.preventDefault();
      goToCheckout();
    },
    true
  );

  window.MaisonBundleFreeShipping = {
    goToCheckout,
  };
})();
